/**
 * event-on-sale: is anything on sale for an event, and if not, why (CC 0436 B). The public event page uses
 * it to hide the registration form, and the event designer to warn at Publish.
 *
 * A tier is on sale when its sales are not closed and now falls inside its sale window. Windows are either
 * absolute (fromUtc–toUtc) or relative to the event's start (fromDaysBefore–toDaysBefore). A tier with no
 * window information counts as on sale, so an older server or tier reads as it does today.
 *
 * NO TIERS IS OPEN (CC 0437): an event with no tickets registers on capacity alone (a free, capacity-only
 * event), so an empty or absent tier list never hides the form. Only an event that HAS tiers, none of them
 * on sale now, is not open.
 */
export type SaleWindow =
  | { kind: "absolute"; fromUtc: string; toUtc: string }
  | { kind: "relative"; fromDaysBefore: number; toDaysBefore: number };

export interface SaleTier {
  /** The public read's own answer (proposed in CC 0436's packet), when the server sends it. */
  onSale?: boolean;
  onSaleFrom?: string | null;
  onSaleUntil?: string | null;
  /** The designer's tier (ticket groups v2). */
  salesClosed?: boolean;
  saleWindow?: SaleWindow | null;
}

export type OnSaleState =
  | { state: "on-sale" }
  | { state: "not-yet"; opensAt: string | null }
  | { state: "closed" };

const DAY = 86_400_000;
const ms = (iso: string | null | undefined) => { const v = iso ? Date.parse(iso) : NaN; return Number.isFinite(v) ? v : null; };

function windowOf(t: SaleTier, eventStart: string | null | undefined): { from: number | null; until: number | null } | null {
  if (t.onSaleFrom !== undefined || t.onSaleUntil !== undefined) return { from: ms(t.onSaleFrom ?? null), until: ms(t.onSaleUntil ?? null) };
  const w = t.saleWindow;
  if (!w) return null;
  if (w.kind === "absolute") return { from: ms(w.fromUtc), until: ms(w.toUtc) };
  const start = ms(eventStart);
  if (start === null) return null;
  return { from: start - w.fromDaysBefore * DAY, until: start - w.toDaysBefore * DAY };
}

/** One tier's state at `now`. */
export function tierSaleState(t: SaleTier, eventStart: string | null | undefined, now = Date.now()): OnSaleState {
  if (t.onSale === true) return { state: "on-sale" };
  if (t.salesClosed === true) return { state: "closed" };
  const w = windowOf(t, eventStart);
  if (t.onSale === false) {
    if (w && w.from !== null && w.from > now) return { state: "not-yet", opensAt: new Date(w.from).toISOString() };
    return w && w.until !== null && w.until <= now ? { state: "closed" } : { state: "not-yet", opensAt: null };
  }
  if (!w) return { state: "on-sale" };
  if (w.from !== null && now < w.from) return { state: "not-yet", opensAt: new Date(w.from).toISOString() };
  if (w.until !== null && now >= w.until) return { state: "closed" };
  return { state: "on-sale" };
}

/** The event as a whole: on sale with no tiers (capacity only) or when any tier is; otherwise the earliest
 *  opening, or closed. */
export function eventSaleState(tiers: SaleTier[] | null | undefined, eventStart: string | null | undefined, now = Date.now()): OnSaleState {
  if (!Array.isArray(tiers) || tiers.length === 0) return { state: "on-sale" };
  const states = tiers.map((t) => tierSaleState(t, eventStart, now));
  if (states.some((s) => s.state === "on-sale")) return { state: "on-sale" };
  const opens = states.filter((s): s is { state: "not-yet"; opensAt: string | null } => s.state === "not-yet");
  if (opens.length) {
    const dated = opens.map((s) => s.opensAt).filter((v): v is string => !!v).sort();
    return { state: "not-yet", opensAt: dated[0] ?? null };
  }
  return { state: "closed" };
}
